<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, reactive, watch } from 'vue'
import { onMounted, onBeforeUnmount } from 'vue'
import { ChevronDownIcon, XMarkIcon } from 'Common/components/elements/icons/Icons'
import { Sizes } from 'Modules/types/filterTypes'

const props = defineProps<{
  sizes: Sizes
  label: string
}>()

const emits = defineEmits<{
  (e: 'update:sizes', sizes: Sizes): void
}>()

const defaultSizes = {
  area: {
    min: null,
    max: null,
  },
  length: {
    min: null,
    max: null,
  },
}
const isOpen = ref(false)
const isButtonMousedOver = ref(false)
const localSizes = reactive({ ...defaultSizes, ...props.sizes })

// Toggle dropdown visibility
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

// Close dropdown when clicking outside
const dropdownRef = ref<HTMLElement | null>(null)
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

const hasDefinedValues = computed(() => {
  return Object.values(props.sizes).some((subObj) => {
    return (subObj.min !== undefined && subObj.min !== null) || (subObj.max !== undefined && subObj.max !== null)
  })
})

const toggleTextClass = computed(() => {
  if (hasDefinedValues.value === true) return 'text-white'
  return ''
})

const toggleIconClass = computed(() => {
  let classesArray = []
  if (isOpen.value) classesArray.push('rotate-180 text-white')
  if (isOpen.value || isButtonMousedOver.value) classesArray.push('text-white')
  return classesArray.join(' ')
})

const handleMouseEnter = () => {
  isButtonMousedOver.value = true
}

const handleMouseLeave = () => {
  isButtonMousedOver.value = false
}

const handleClearButtonClick = () => {
  clearSelections()
}

const clearSelections = () => {
  localSizes.area.min = null
  localSizes.area.max = null
  localSizes.length.min = null
  localSizes.length.max = null
  emits('update:sizes', {
    area: {
      min: null,
      max: null,
    },
    length: {
      min: null,
      max: null,
    },
  })
}

const handleSubmitClick = () => {
  emits('update:sizes', { ...localSizes })
  toggleDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  dropdownRef.value = null
})

watch(
  () => props.sizes,
  (newSizes) => {
    localSizes.area.min = newSizes.area.min
    localSizes.area.max = newSizes.area.max
    localSizes.length.min = newSizes.length.min
    localSizes.length.max = newSizes.length.max
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <div ref="dropdownRef" class="relative w-full">
    <button
      type="button"
      class="w-full px-4 py-2 flex items-center justify-between text-left bg-transparent rounded-none hover:outline-none focus-visible:outline-none focus:border-0 focus:outline-none"
      @click="toggleDropdown"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <span class="block mr-2 truncate text-gray-700" :class="toggleTextClass">
        {{ props.label }}
      </span>
      <span v-if="hasDefinedValues === false" class="px-1" :class="toggleIconClass" @click="toggleDropdown">
        <ChevronDownIcon />
      </span>
      <span v-else class="px-1 text-gray-700 hover:text-white hover:cursor-pointer" @click="handleClearButtonClick">
        <XMarkIcon />
      </span>
    </button>

    <!-- Dropdown Menu -->
    <transition name="fade">
      <div
        v-if="isOpen"
        class="absolute z-20 w-60 p-2 flex flex-col items-center justify-center mt-2 bg-zinc-200 border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto"
      >
        <div class="flex flex-col items-start justify-center p-2">
          <span class="mb-2 text-gray-700">Area (cm²)</span>
          <div class="flex items-center justify-between">
            <el-input v-model="localSizes.area.min" class="rounded-md" type="number" placeholder="Min" :min="0" />
            <span class="mx-2">-</span>
            <el-input v-model="localSizes.area.max" type="number" placeholder="Max" :min="0" />
          </div>
        </div>
        <el-divider />
        <div class="flex flex-col items-start justify-center p-2">
          <span class="mb-2 text-gray-700">Length (cm)</span>
          <div class="flex items-center justify-between">
            <el-input v-model="localSizes.length.min" type="number" placeholder="min" :min="0" />
            <span class="mx-2">-</span>
            <el-input v-model="localSizes.length.max" type="number" placeholder="min" :min="0" />
          </div>
        </div>
        <el-divider />
        <div class="h-full w-full px-2 flex items-center justify-end">
          <el-button size="small" type="primary" @click="handleSubmitClick">Submit</el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.el-divider.el-divider--horizontal {
  margin: 0.5rem 0;
  border: 1px solid lightgray;
}
</style>
